import { useRef } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { BellNotificationItem } from '../api/notifications-queries'
import { useMarkNotificationRead } from '../api/notifications-mutations'
import {
  formatNotificationAbsolute,
  formatNotificationTimeAgo,
} from '../lib/format-notification-time'
import { parseNotificationMessage } from '../lib/parse-notification-message'
import { notificationFullCardSx } from '../styles/notificationsPageStyles'
import { NotificationTypeIcon } from './NotificationTypeIcon'

const MONGO_OBJECT_ID_HEX24 = /^[a-fA-F0-9]{24}$/

function isValidMongoObjectIdString(id: string): boolean {
  return MONGO_OBJECT_ID_HEX24.test(id.trim())
}

type Props = {
  notification: BellNotificationItem
  onNavigate: (url: string) => void
}

const SWIPE_MARK_READ_THRESHOLD_PX = 50

export function NotificationFullCard({ notification, onNavigate }: Readonly<Props>) {
  const { mutate: markRead } = useMarkNotificationRead()
  const touchStartXRef = useRef<number | null>(null)
  const isUnread = !notification.isRead
  const { title, description } = parseNotificationMessage(notification.message)
  const displayTitle = notification.title ?? title
  const displayDescription = notification.description ?? description
  const timeLabel = formatNotificationTimeAgo(notification.createdAt)
  const absoluteTime = formatNotificationAbsolute(notification.createdAt)
  const isClickable = Boolean(notification.relatedUrl)

  const handleClick = () => {
    if (!isClickable) return
    if (isUnread && isValidMongoObjectIdString(notification.id)) {
      markRead(notification.id)
    }
    if (notification.relatedUrl) {
      onNavigate(notification.relatedUrl)
    }
  }

  const handleTouchStart = (clientX: number) => {
    touchStartXRef.current = clientX
  }

  const handleTouchEnd = (clientX: number) => {
    if (touchStartXRef.current === null || !isUnread) return
    const deltaX = clientX - touchStartXRef.current
    touchStartXRef.current = null
    if (deltaX < -SWIPE_MARK_READ_THRESHOLD_PX && isValidMongoObjectIdString(notification.id)) {
      markRead(notification.id)
    }
  }

  const swipeHandlers = {
    onTouchStart: (event: React.TouchEvent) => handleTouchStart(event.touches[0]?.clientX ?? 0),
    onTouchEnd: (event: React.TouchEvent) =>
      handleTouchEnd(event.changedTouches[0]?.clientX ?? 0),
  }

  const content = (
    <>
      <NotificationTypeIcon title={displayTitle} />

      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '12px',
            fontWeight: isUnread ? 700 : 400,
            lineHeight: '20px',
            color: '#000000',
          }}
        >
          {displayTitle}
        </Typography>
        {displayDescription ? (
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '16px',
              color: '#000000',
              mt: '4px',
            }}
          >
            {displayDescription}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        {isUnread ? (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#008cd0' }} aria-hidden />
        ) : null}
        {timeLabel ? (
          <Tooltip title={absoluteTime} placement="top">
            <Typography
              component="span"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '12px',
                fontWeight: isUnread ? 500 : 400,
                lineHeight: '16px',
                color: isUnread ? '#008cd0' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              {timeLabel}
            </Typography>
          </Tooltip>
        ) : null}
      </Box>
    </>
  )

  if (isClickable) {
    return (
      <Box
        component="button"
        type="button"
        data-testid={`provider-notification-full-${notification.id}`}
        onClick={handleClick}
        sx={notificationFullCardSx(isUnread, true)}
        {...swipeHandlers}
      >
        {content}
      </Box>
    )
  }

  return (
    <Box
      data-testid={`provider-notification-full-${notification.id}`}
      sx={notificationFullCardSx(isUnread, false)}
      {...swipeHandlers}
    >
      {content}
    </Box>
  )
}
